import React, { useState, useRef, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import "../styles/components/PricingSelector.css";
import livePrices from "../data/prices.live.json";
import testPrices from "../data/prices.test.json";
import EmbeddedCheckoutModal from "./EmbeddedCheckoutModal";

const priceData =
  process.env.REACT_APP_STRIPE_PRICES === "test" ? testPrices : livePrices;
const packages = priceData.pricingPackages;

function PricingSelector() {
  const [billingCycle, setBillingCycle] = useState("annually");
  const [monthlyRequests, setMonthlyRequests] = useState(10000);
  const [showPopup, setShowPopup] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const popupRef = useRef(null);
  const isSliderActive = useRef(false);
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  // Check for intended purchase after authentication
  useEffect(() => {
    const checkForIntendedPurchase = () => {
      const intendedPurchaseStr = sessionStorage.getItem("intendedPurchase");

      if (intendedPurchaseStr) {
        const intendedPurchase = JSON.parse(intendedPurchaseStr);
        const packageToSelect = packages.find(
          (pkg) => pkg.packname === intendedPurchase.package
        );

        if (packageToSelect) {
          if (intendedPurchase.billingCycle !== billingCycle) {
            setBillingCycle(intendedPurchase.billingCycle);
          }

          setTimeout(() => {
            setSelectedPackage(packageToSelect);
            setShowCheckout(true);
          }, 100);
        }

        sessionStorage.removeItem("intendedPurchase");
      }
    };

    if (isAuthenticated) {
      checkForIntendedPurchase();
    }
  }, [isAuthenticated, billingCycle]);

  const handleSliderChange = (value) => {
    isSliderActive.current = true;
    const maxRequests = 10000000;
    const minRequests = 10000;
    const scaledValue = Math.round(
      minRequests * Math.pow(maxRequests / minRequests, value / 100)
    );
    setMonthlyRequests(scaledValue);
  };

  const getSliderValue = () => {
    const maxRequests = 10000000;
    const minRequests = 10000;
    return (
      (Math.log(monthlyRequests / minRequests) /
        Math.log(maxRequests / minRequests)) *
      100
    );
  };

  const getHighlightedPackage = () => {
    if (monthlyRequests >= 10000000) {
      return packages[packages.length - 1];
    }
    return (
      packages.find((pkg) => monthlyRequests <= pkg.requests) ||
      packages[packages.length - 1]
    );
  };

  const formatPrice = (price) => {
    if (price === 0) return "Gratis";
    const discounted = billingCycle === "annually" ? price * 0.85 : price;
    const formattedDiscounted = `kr ${Math.round(discounted).toLocaleString(
      "no-NO"
    )},-`;
    const formattedOriginal = `kr ${Math.round(price).toLocaleString(
      "no-NO"
    )},-`;

    if (billingCycle === "annually") {
      return (
        <>
          <span
            style={{
              textDecoration: "line-through",
              color: "gray",
              marginRight: "0.5rem",
            }}
          >
            {formattedOriginal}
          </span>
          <br />
          {formattedDiscounted}
        </>
      );
    }
    return formattedDiscounted;
  };

  const handlePackageSelect = async (pkg) => {
    if (pkg.price === 0) {
      if (isAuthenticated) {
        navigate("/dashbord");
      } else {
        await loginWithRedirect({
          appState: {
            returnTo: "/dashbord",
            action: "freeSignup",
          },
        });
      }
      return;
    }

    if (pkg.title === "Enterprise") {
      window.location.href = pkg.contactLink;
      return;
    }

    if (!isAuthenticated) {
      sessionStorage.setItem(
        "intendedPurchase",
        JSON.stringify({
          package: pkg.packname,
          billingCycle: billingCycle,
        })
      );

      await loginWithRedirect({
        appState: {
          returnTo: `/priser`,
          action: "purchase",
        },
      });
      return;
    }

    setSelectedPackage(pkg);
    setShowCheckout(true);
  };

  // Check if we have a selected package in appState after login redirect
  useEffect(() => {
    const checkForPostLoginAction = () => {
      // You might want to implement logic here to check URL parameters or appState
      // to automatically open the checkout modal if the user was redirected from login
      // This would require additional state management
    };

    checkForPostLoginAction();
  }, [isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  const highlightedPackage = getHighlightedPackage();

  useEffect(() => {
    if (isSliderActive.current) {
      isSliderActive.current = false;
      return;
    }

    const highlightedElement = document.querySelector(".package.highlighted");
    if (highlightedElement) {
      highlightedElement.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  }, [highlightedPackage]);

  return (
    <div className="pricing-selector">
      {showCheckout && selectedPackage && (
        <EmbeddedCheckoutModal
          package={selectedPackage}
          billingCycle={billingCycle}
          onClose={() => setShowCheckout(false)}
        />
      )}

      {showPopup && (
        <div className="popup">
          <div className="popup-content" ref={popupRef}>
            <h2>Hvordan finne riktig pakke</h2>
            <p>For å finne den riktige pakken, vurder følgende:</p>
            <ol>
              <li>
                Hvor mange hendelser en gjennomsnittlig bruker gjør
                <br />
                (f.eks. sidevisning, klikk, kjøp)
              </li>
              <li>Hvor mange brukere du har per måned</li>
              <li>
                Hvor mange kanaler du vil sende data til (Google, Meta, TikTok
                osv.)
              </li>
            </ol>
            <p>
              <h3>Formel:</h3>
              Gjennomsnittlige hendelser × Brukere × Kanaler = Ditt behov
            </p>
            <h3>Eksempel</h3>
            <ul>
              <li>10 gjennomsnittlige hendelser per bruker</li>
              <li>1 000 brukere</li>
              <li>5 kanaler</li>
            </ul>
            <p>
              10 × 1 000 × 5 = <strong>50 000 forespørsler</strong> per måned
            </p>
          </div>
        </div>
      )}

      <div className="help-button">
        <button onClick={() => setShowPopup(true)}>
          Hvor mange forespørsler trenger jeg?
        </button>
      </div>

      <div className="billing-toggle">
        <label>
          <input
            type="radio"
            name="billing"
            value="monthly"
            checked={billingCycle === "monthly"}
            onChange={() => setBillingCycle("monthly")}
          />
          Månedlig
        </label>
        <label>
          <input
            type="radio"
            name="billing"
            value="annually"
            checked={billingCycle === "annually"}
            onChange={() => setBillingCycle("annually")}
          />
          Årlig (15% avslag)
        </label>
      </div>

      <div className="request-slider">
        <label>
          Antall månedlige forespørsler:{" "}
          {monthlyRequests >= 10000000
            ? "10 000 000+"
            : monthlyRequests.toLocaleString("no-NO")}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={getSliderValue()}
          onChange={(e) => handleSliderChange(e.target.value)}
          style={{
            "--slider-track": `linear-gradient(to right, var(--main) 0%, var(--main) ${getSliderValue()}%, var(--white) ${getSliderValue()}%, var(--white) 100%)`,
          }}
        />
      </div>

      <div className="packages-container">
        <div className="packages">
          {packages.map((pkg) => {
            const isHighlighted = pkg.title === highlightedPackage.title;
            return (
              <div
                key={pkg.title}
                className={`package ${isHighlighted ? "highlighted" : ""}`}
              >
                <h3>{pkg.title}</h3>
                <p className="price">
                  {pkg.price !== null ? formatPrice(pkg.price) : "Ta kontakt"}
                </p>
                <p className="requests">
                  {typeof pkg.requests === "string"
                    ? pkg.requests
                    : `Opp til ${pkg.requests.toLocaleString(
                        "no-NO"
                      )} forespørseler`}
                </p>
                <div className="separator"></div>
                <ul>
                  {pkg.bulletpoints.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
                <button onClick={() => handlePackageSelect(pkg)}>
                  {pkg.cta}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PricingSelector;
